import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-scroll';
import { Phone, Menu, X, Mail } from 'lucide-react';
import { FaWhatsapp, FaFacebookF, FaInstagram } from 'react-icons/fa';
import { contactInfo } from '@/mock';

const OnePageNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { name: 'Accueil', to: 'accueil' },
    { name: 'Services', to: 'services' },
    { name: 'Tarifs', to: 'tarifs' },
    { name: 'Témoignages', to: 'temoignages' },
    { name: 'Contact', to: 'contact' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-black/80 backdrop-blur-sm'
    } border-b border-rose-900/20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link 
            to="accueil" 
            smooth={true} 
            duration={500}
            className="flex items-center cursor-pointer"
          >
            <span className="text-4xl font-script text-rose-200">LadyGo</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                smooth={true}
                duration={500}
                offset={-80}
                spy={true}
                activeClass="text-rose-200"
                className="text-base font-medium tracking-wide text-gray-300 hover:text-rose-200 cursor-pointer transition-colors duration-300"
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Contact Icons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="https://www.facebook.com/profile.php?id=61591822262375"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
              aria-label="Facebook"
            >
              <FaFacebookF className="w-5 h-5" />
            </a>
            <a
              href="https://www.instagram.com/ladygovtc/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
              aria-label="Instagram"
            >
              <FaInstagram className="w-5 h-5" />
            </a>
            <a
              href={`https://wa.me/${contactInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
            >
              <FaWhatsapp className="w-5 h-5" />
            </a>
            <a
              href={`mailto:${contactInfo.email}`}
              className="p-2 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
            >
              <Mail className="w-5 h-5" />
            </a>
            <a
              href={`tel:${contactInfo.phoneFormatted}`}
              className="flex items-center space-x-2 px-5 py-2 rounded-full bg-rose-200 text-gray-900 hover:bg-rose-300 transition-all duration-300"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">{contactInfo.phone}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-rose-200 hover:bg-rose-200/10 rounded-lg transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-black/98 border-t border-rose-900/20">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                smooth={true}
                duration={500}
                offset={-80}
                onClick={() => setIsOpen(false)}
                className="block py-3 px-4 rounded-lg text-gray-300 hover:bg-rose-200/5 hover:text-rose-200 cursor-pointer transition-colors"
              >
                {link.name}
              </Link>
            ))}
            
            {/* Social Icons Mobile */}
            <div className="pt-4 pb-2 flex justify-center space-x-4">
              <a
                href="https://www.facebook.com/profile.php?id=61591822262375"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
                aria-label="Facebook"
              >
                <FaFacebookF className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/ladygovtc/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
                aria-label="Instagram"
              >
                <FaInstagram className="w-5 h-5" />
              </a>
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <FaWhatsapp className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="p-3 rounded-full border border-rose-200/30 text-rose-200 hover:bg-rose-200/10 transition-all duration-300"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
            
            {/* Phone Button */}
            <div className="pt-2 flex justify-center">
              <a
                href={`tel:${contactInfo.phoneFormatted}`}
                className="flex items-center space-x-2 px-6 py-3 rounded-full bg-rose-200 text-gray-900 hover:bg-rose-300 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">{contactInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default OnePageNavbar;
